/**
 * Company-Specific Actions
 * Actions defined specifically for the companies entity
 * Strongly typed to Companies row type
 * Uses shared builders to eliminate duplication
 */

import type { Companies } from "@/src/gql/graphql";
import type { DetailActionStrict } from "@/lib/actions/types/detail-action";
import {
  ACTION_ICONS,
  ACTION_VARIANTS,
  ROLE_COMBINATIONS,
} from "@/lib/actions/types/action-properties";
import {
  universalDeleteAction,
  universalSendEmailAction,
  universalLogActivityAction,
  universalAskMeridianAction,
  universalViewDetailsAction,
  universalCreateTaskAction,
  universalScheduleMeetingAction,
  universalViewCompanyAction,
  universalViewSupportInboxAction,
  universalConfigureAutomatedTasksAction,
  universalViewAuditHistoryAction,
} from "@/lib/actions/definitions/universal-actions";
import {
  createStatusAction,
  createSendTextMessageAction,
  createGenerateContractAction,
  createManageAccessAction,
  createConsolidateAction,
  createResearchCompanyAction,
  createFindDecisionMakersAction,
  createMakeContactAction,
  createLinkContactAction,
  createEnrichCompanyAction,
  createLogDealAction,
} from "@/lib/actions/definitions/shared";
import {
  createSendToSlackAction,
  createSendToSalesChannelAction,
  createOpenSlackChannelAction,
} from "@/lib/actions/definitions/shared/slack-actions";
import { createEnrollInSequenceAction } from "@/lib/actions/definitions/shared/sequence-actions";

/**
 * Complete company actions array
 * Combines universal and company-specific actions
 */
export const companyDetailActions: DetailActionStrict<Companies>[] = [
  // AI ASSISTANT ACTIONS
  universalAskMeridianAction<Companies>(),

  // PRIMARY ACTIONS (Header)
  // No Edit action — fields are edited inline on the detail page; the old
  // modal-based edit was an unmounted no-op (see deal-actions for the full note).
  universalSendEmailAction<Companies>({ primary: true }),
  universalLogActivityAction<Companies>({ primary: true }),

  // SHARED ACTIONS
  universalViewDetailsAction<Companies>("viewCompany"),

  // SECONDARY ACTIONS (Menu)
  createMakeContactAction<Companies>(),
  createLinkContactAction<Companies>(),
  createLogDealAction<Companies>(),
  // Sales cadence — gated to the rollout allowlist at useActionFiltering (by email).
  createEnrollInSequenceAction<Companies>({
    entity: "company",
    allowedRoles: [...ROLE_COMBINATIONS.ADMIN_ONLY],
  }),
  // Admin-only while rollout stabilizes. Walks company → contacts (multi-pick).
  createSendTextMessageAction<Companies>({
    allowedRoles: [...ROLE_COMBINATIONS.ADMIN_ONLY],
  }),
  universalCreateTaskAction<Companies>({ bulkable: true }),
  universalScheduleMeetingAction<Companies>(),
  createGenerateContractAction<Companies>(),
  createManageAccessAction<Companies>(),
  createEnrichCompanyAction<Companies>(),
  // Re-run lead scoring on demand (also runs automatically on company create + status
  // change). Refreshes the fit/score/rec tags; managed tags only, never touches others.
  {
    id: "rescore-lead",
    label: "Re-score lead",
    icon: ACTION_ICONS.SPARKLES,
    variant: ACTION_VARIANTS.OUTLINE,
    action: "rescoreLead",
    allowedRoles: [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
    tooltip: "Re-run lead scoring and refresh the fit/score tags for this company",
    bulkable: true,
  },
  // Enroll a Google support group for this company (design doc §2.4). Employee+admin.
  {
    id: "enroll-support-group",
    label: "Enroll support group",
    icon: ACTION_ICONS.MAIL,
    variant: ACTION_VARIANTS.OUTLINE,
    action: "enrollSupportGroup",
    allowedRoles: [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
    tooltip: "Create or bind a Google support group so inbound support mail syncs to the Hub",
  },
  // View the live support-group inbox (triage before a ticket). Low-prominence.
  universalViewSupportInboxAction<Companies>(),
  createResearchCompanyAction<Companies>(),
  createFindDecisionMakersAction<Companies>(),
  universalConfigureAutomatedTasksAction<Companies>(),
  createSendToSlackAction<Companies>(),
  createSendToSalesChannelAction<Companies>(),
  createOpenSlackChannelAction<Companies>(),
  universalDeleteAction<Companies>({ bulkable: true }),
  createConsolidateAction<Companies>({
    id: "consolidate-company",
    label: "Consolidate",
    action: "consolidateCompany",
    tooltip: "Merge this company with another company",
  }),
  // History & Audit — read-only forensic view + per-event restore. Staff only;
  // registered here so it reaches BOTH the ⋯ button and right-click on every
  // surface that renders a company (ENTITY_TABLE_ROW_ACTIONS).
  universalViewAuditHistoryAction<Companies>(),
];
